//-------------------------------------------------------------------------------------------------- Imports
import { Button, Input, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { StyleSheet } from "../StyleSheet";
import { images } from "../routes/ImageRoutes";
import { getGeneralData, fillGeneralDataSlice } from "../stores/slices/generalDataSlice";
import { getConfigurableServiceState } from "../stores/slices/configurableServicesSlice";
import { AppDispatch } from "../stores/store";
import { updateCurp } from "../methods/Methods";
import { getCurpValidationError } from "../common/functions";
const { Text } = Typography;

//-------------------------------------------------------------------------------------------------- Interfaces
interface ResultState {
    status: "success" | "error";
    message?: string;
}

//-------------------------------------------------------------------------------------------------- Component
const UpdateCurpModal = () => {
    const dispatch = useDispatch<AppDispatch>();
    const generalData = useSelector(getGeneralData);
    const configurableService = useSelector(getConfigurableServiceState);
    const [showModal, setShowModal] = useState(false);
    const [curp, setCurp] = useState("");
    const [curpError, setCurpError] = useState("");
    const [saving, setSaving] = useState(false);
    const [result, setResult] = useState<ResultState | null>(null);

    useEffect(() => {
        const curpFeatureEnabled = !!configurableService.dataList.find(
            (service) => service.name === "VIEW_UPDATE_CURP"
        )?.enabled;
        setShowModal(
            curpFeatureEnabled &&
            generalData.selectedContract?.megamovil === 1 &&
            _.isEmpty(generalData.selectedContract?.curp)
        );
    }, [generalData.selectedContract, configurableService.dataList]);

    const handleChangeCurp = (value: string) => {
        const upperCaseValue = value.toUpperCase();
        setCurp(upperCaseValue);
        setCurpError(getCurpValidationError(upperCaseValue));
    };

    const isDisabled = !curp || !!curpError;

    const handleSaveCurp = () => {
        if (!generalData.selectedContract) {
            return;
        }

        const { sucursal, suscriptor } = generalData.selectedContract;
        setSaving(true);
        updateCurp({ sucursal, suscriptor, curp })
            .then(async (response) => {
                const data = response.data.data[0];
                if (data.status === "EXITO") {
                    await dispatch(fillGeneralDataSlice({ clientId: sucursal + suscriptor }));
                    setResult({ status: "success", message: data.message });
                } else {
                    setResult({ status: "error", message: data.message });
                }
            })
            .catch(() => {
                setResult({
                    status: "error",
                    message: "Hubo un error al procesar su solicitud. \nIntente más tarde."
                });
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const handleFinishSuccess = () => {
        setResult(null);
        setShowModal(false);
    };

    const handleRetry = () => {
        setResult(null);
    };

    if (!showModal) {
        return null;
    }

    return (
        <div style={styles.mainContainer}>
            <div style={styles.cardContainer}>
                {
                    !result ? (
                        <CloseOutlined
                            style={styles.closeIcon}
                            onClick={() => setShowModal(false)}
                        />
                    ) : null
                }
                <img style={styles.imageLogo} src={images.megaLogoMR} alt="Logo megacable" />
                {
                    result ? (
                        <>
                            <img
                                style={styles.resultIcon}
                                src={result.status === "success" ? images.iconSuccess : images.iconError}
                                alt={result.status === "success" ? "Éxito" : "Error"}
                            />
                            <h2 style={styles.title}>
                                {
                                    result.status === "success"
                                        ? "¡Tus datos se actualizaron correctamente!"
                                        : "No pudimos actualizar tus datos"
                                }
                            </h2>
                            {
                                result.status === "error" && result.message ? (
                                    <Text style={styles.description}>{result.message}</Text>
                                ) : null
                            }
                            <Button
                                style={styles.buttonPrimary}
                                onClick={result.status === "success" ? handleFinishSuccess : handleRetry}
                            >
                                {result.status === "success" ? "Continuar" : "Reintentar"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <h2 style={styles.title}>Actualizar tus datos</h2>
                            <Text style={styles.description}>
                                Con la finalidad de mantenerte informado con los beneficios que tenemos para tí, te
                                invitamos a actualizar tus datos de contrato.
                            </Text>
                            <div style={styles.inputContainer}>
                                <Input
                                    placeholder="Ingresa tu CURP"
                                    value={curp}
                                    style={styles.inputText}
                                    onChange={(event) => handleChangeCurp(event.target.value)}
                                    maxLength={18}
                                    status={curpError ? "error" : ""}
                                />
                                <Text type="danger" style={styles.emptyFieldError}>
                                    {curpError || ""}
                                </Text>
                            </div>
                            <Button
                                style={styles.buttonPrimary}
                                loading={saving}
                                disabled={isDisabled || saving}
                                onClick={handleSaveCurp}
                            >
                                Guardar mis datos
                            </Button>
                            <Text style={styles.privacyText}>
                                Consulta nuestro aviso de privacidad{" "}
                                <a
                                    style={styles.textLink}
                                    href="https://www.megacable.com.mx/privacidad"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    aquí
                                </a>
                            </Text>
                        </>
                    )
                }
            </div>
        </div>
    );
};

//-------------------------------------------------------------------------------------------------- Styles
const styles = StyleSheet.create({
    mainContainer: {
        position: "absolute",
        display: "flex",
        width: "100%",
        height: "100%",
        zIndex: 100,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "rgb(0 0 0 / 60%)",
    },
    cardContainer: {
        position: "relative",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        width: 420,
        padding: "40px 30px",
        borderRadius: 15,
        backgroundColor: "#ffffff",
        boxShadow: "0px 0px 8px #00000029",
    },
    closeIcon: {
        position: "absolute",
        top: 15,
        right: 15,
        fontSize: 16,
        color: "#757982",
        cursor: "pointer",
    },
    imageLogo: {
        maxHeight: 40,
        maxWidth: "100%",
        marginBottom: 15,
    },
    resultIcon: {
        width: 72,
        height: 72,
        marginBottom: 15,
    },
    title: {
        fontSize: 22,
        fontWeight: "bold",
        textAlign: "center",
        margin: "15px 0px",
        color: "#000000",
    },
    description: {
        fontSize: 14,
        textAlign: "center",
        color: "#000000",
        marginBottom: 30,
    },
    inputContainer: {
        width: "100%",
    },
    inputText: {
        color: "#000000",
        borderColor: "#757982",
        fontSize: 14,
    },
    emptyFieldError: {
        fontSize: 11,
    },
    buttonPrimary: {
        backgroundColor: "#0683ff",
        borderRadius: 30,
        color: "white",
        border: "none",
        padding: "0px 30px",
        height: 45,
        marginTop: 20,
        width: "100%",
    },
    privacyText: {
        fontSize: 13,
        textAlign: "center",
        color: "#000000",
        marginTop: 30,
    },
    textLink: {
        color: "#0683ff",
        fontSize: 13,
        marginTop: 15,
        cursor: "pointer",
    },
});

//-------------------------------------------------------------------------------------------------- Exports
export default UpdateCurpModal;
